// Three.js bootstrap: WebGL2 renderer sized to 1920x1080 with transparent background
// so OBS composites the scene over the streamer's video feed.
//
// - devicePixelRatio capped at 2 to protect the per-frame budget.
// - dt capped at 1ms stepping per rAF tick to keep animation math stable when the tab
//   loses focus.
// - Orthographic camera keeps world units ≈ pixels.
//
// v1.3 camera-corner layout: the stage itself remains 1920x1080. main.ts reads
// the per-tenant camera_position (with `?cam=` URL override) and applies the
// safe-area transform (see lib/safe-area.ts) by re-anchoring scene group
// positions. Scenes themselves stay coordinate-agnostic.

import {
  Clock,
  OrthographicCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import { STAGE_HEIGHT, STAGE_WIDTH } from './lib/theme.js';

export interface StageHandle {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: OrthographicCamera;
  /** Add a per-frame tick callback. Returns an unsubscribe. */
  addTick: (cb: (dt: number, now: number) => void) => () => void;
  /** Force a re-render (used when scene graph changes outside of rAF). */
  invalidate: () => void;
  dispose: () => void;
}

export interface StageOpts {
  mount: HTMLElement;
  /** Cap on devicePixelRatio; default 2. */
  maxPixelRatio?: number;
  /** Cap on a single dt step in seconds; default 0.1 (prevents long-pause jumps). */
  maxStepSeconds?: number;
}

export function createStage(opts: StageOpts): StageHandle {
  const maxDpr = opts.maxPixelRatio ?? 2;
  const maxStep = opts.maxStepSeconds ?? 0.1;

  const renderer = new WebGLRenderer({
    antialias: true,
    alpha: true,
    premultipliedAlpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxDpr));
  renderer.setSize(STAGE_WIDTH, STAGE_HEIGHT, false);
  const c = renderer.domElement;
  c.style.position = 'absolute';
  c.style.inset = '0';
  c.style.width = '100%';
  c.style.height = '100%';
  c.setAttribute('data-testid', 'stage-canvas');
  opts.mount.appendChild(c);

  const scene = new Scene();

  const halfW = STAGE_WIDTH / 2;
  const halfH = STAGE_HEIGHT / 2;
  const camera = new OrthographicCamera(-halfW, halfW, halfH, -halfH, -2000, 2000);
  camera.position.set(0, 0, 1000);

  const ticks = new Set<(dt: number, now: number) => void>();
  const clock = new Clock();

  let raf = 0;
  let running = true;
  function frame(): void {
    if (!running) return;
    const dt = Math.min(clock.getDelta(), maxStep);
    const now = clock.elapsedTime;
    for (const cb of ticks) cb(dt, now);
    renderer.render(scene, camera);
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  function handleResize(): void {
    // Keep internal resolution fixed at 1920x1080; CSS scales for preview.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxDpr));
  }
  window.addEventListener('resize', handleResize);

  return {
    renderer,
    scene,
    camera,
    addTick(cb) {
      ticks.add(cb);
      return () => {
        ticks.delete(cb);
      };
    },
    invalidate() {
      renderer.render(scene, camera);
    },
    dispose() {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      c.remove();
    },
  };
}
